import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import '../../services/GlobalVariables'
import axios from 'axios'

const API_URL = global.SET_API_URL + 'posBusiness/'

const initialState = {
    tables: [],
    employees: [],
    heldOrders: [],
    currentOrder: null,
    orderID: null,
    isLoading: false,
    isError: false,
    isSuccess: false,
    message: ""
}

// Get all tables with reservation status
export const getTables = createAsyncThunk('posBusiness/getTables', async (_, thunkAPI) => {
    try {
        const result = await axios.get(API_URL + 'getTables')
        return result.data.tables
    } catch (error) {
        const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString()
        return thunkAPI.rejectWithValue(message)
    }
})

// Get employees for waiter/rider dropdown
export const getEmployees = createAsyncThunk('posBusiness/getEmployees', async (_, thunkAPI) => {
    try {
        const result = await axios.get(API_URL + 'getEmployees')
        return result.data.employees
    } catch (error) {
        const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString()
        return thunkAPI.rejectWithValue(message)
    }
})

// Get all held orders from database
export const getHeldOrders = createAsyncThunk('posBusiness/getHeldOrders', async (_, thunkAPI) => {
    try {
        const result = await axios.get(API_URL + 'getHeldOrders')
        return result.data.heldOrders
    } catch (error) {
        const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString()
        return thunkAPI.rejectWithValue(message)
    }
})

// Assign a table
export const assignTable = createAsyncThunk('posBusiness/assignTable', async (tableData, thunkAPI) => {
    try {
        const result = await axios.post(API_URL + 'assignTable', tableData)
        return result.data
    } catch (error) {
        const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString()
        return thunkAPI.rejectWithValue(message)
    }
})

// Release a table
export const releaseTable = createAsyncThunk('posBusiness/releaseTable', async (tableData, thunkAPI) => {
    try {
        const result = await axios.post(API_URL + 'releaseTable', tableData)
        return result.data
    } catch (error) {
        const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString()
        return thunkAPI.rejectWithValue(message)
    }
})

// Transfer a table (change order table)
export const transferTable = createAsyncThunk('posBusiness/transferTable', async (tableData, thunkAPI) => {
    try {
        const result = await axios.post(API_URL + 'transferTable', tableData)
        return result.data
    } catch (error) {
        const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString()
        return thunkAPI.rejectWithValue(message)
    }
})

// Save complete order
export const saveOrder = createAsyncThunk('posBusiness/saveOrder', async (orderData, thunkAPI) => {
    try {
        const result = await axios.post(API_URL + 'saveOrder', orderData)
        return result.data
    } catch (error) {
        const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString()
        return thunkAPI.rejectWithValue(message)
    }
})

// Update existing order (for resumed orders)
export const updateOrder = createAsyncThunk('posBusiness/updateOrder', async (orderData, thunkAPI) => {
    try {
        const result = await axios.post(API_URL + 'updateOrder', orderData)
        return result.data
    } catch (error) {
        const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString()
        return thunkAPI.rejectWithValue(message)
    }
})

// Hold/park an order
export const holdOrder = createAsyncThunk('posBusiness/holdOrder', async (holdData, thunkAPI) => {
    try {
        const result = await axios.post(API_URL + 'holdOrder', holdData)
        return result.data
    } catch (error) {
        const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString()
        return thunkAPI.rejectWithValue(message)
    }
})

// Resume a held order
export const resumeOrder = createAsyncThunk('posBusiness/resumeOrder', async (resumeData, thunkAPI) => {
    try {
        const result = await axios.post(API_URL + 'resumeOrder', resumeData)
        return result.data
    } catch (error) {
        const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString()
        return thunkAPI.rejectWithValue(message)
    }
})

// Resume a held order by table label
export const resumeOrderByTable = createAsyncThunk('posBusiness/resumeOrderByTable', async (resumeData, thunkAPI) => {
    try {
        const result = await axios.post(API_URL + 'resumeOrderByTable', resumeData)
        return result.data
    } catch (error) {
        const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString()
        return thunkAPI.rejectWithValue(message)
    }
})

export const posBusinessSlice = createSlice({
    name: 'posBusiness',
    initialState,
    reducers: {
        reset: (state) => {
            state.tables = []
            state.employees = []
            state.heldOrders = []
            state.currentOrder = null
            state.orderID = null
            state.isLoading = false
            state.isError = false
            state.isSuccess = false
            state.message = ""
        },
        clearOrder: (state) => {
            state.currentOrder = null
            state.orderID = null
            state.isSuccess = false
            state.message = ""
        }
    },
    extraReducers: (builder) => {
        builder
            // Get Tables
            .addCase(getTables.pending, (state) => {
                state.isLoading = true
            })
            .addCase(getTables.fulfilled, (state, action) => {
                state.isLoading = false
                state.isSuccess = true
                state.tables = action.payload
            })
            .addCase(getTables.rejected, (state, action) => {
                state.isLoading = false
                state.isError = true
                state.message = action.payload
            })
            // Get Employees
            .addCase(getEmployees.pending, (state) => {
                state.isLoading = true
            })
            .addCase(getEmployees.fulfilled, (state, action) => {
                state.isLoading = false
                state.isSuccess = true
                state.employees = action.payload
            })
            .addCase(getEmployees.rejected, (state, action) => {
                state.isLoading = false
                state.isError = true
                state.message = action.payload
            })
            // Assign Table
            .addCase(assignTable.pending, (state) => {
                state.isLoading = true
            })
            .addCase(assignTable.fulfilled, (state, action) => {
                state.isLoading = false
                state.isSuccess = true
                state.message = action.payload.message
            })
            .addCase(assignTable.rejected, (state, action) => {
                state.isLoading = false
                state.isError = true
                state.message = action.payload
            })
            // Release Table
            .addCase(releaseTable.pending, (state) => {
                state.isLoading = true
            })
            .addCase(releaseTable.fulfilled, (state, action) => {
                state.isLoading = false
                state.isSuccess = true
                state.message = action.payload.message
            })
            .addCase(releaseTable.rejected, (state, action) => {
                state.isLoading = false
                state.isError = true
                state.message = action.payload
            })
            // Save Order
            .addCase(saveOrder.pending, (state) => {
                state.isLoading = true
            })
            .addCase(saveOrder.fulfilled, (state, action) => {
                state.isLoading = false
                state.isSuccess = true
                state.orderID = action.payload.orderID
                state.currentOrder = action.payload.orderDetails
                state.message = action.payload.message
            })
            .addCase(saveOrder.rejected, (state, action) => {
                state.isLoading = false
                state.isError = true
                state.message = action.payload
            })
            // Update Order
            .addCase(updateOrder.pending, (state) => {
                state.isLoading = true
            })
            .addCase(updateOrder.fulfilled, (state, action) => {
                state.isLoading = false
                state.isSuccess = true
                state.message = action.payload.message
            })
            .addCase(updateOrder.rejected, (state, action) => {
                state.isLoading = false
                state.isError = true
                state.message = action.payload
            })
            // Hold Order
            .addCase(holdOrder.pending, (state) => {
                state.isLoading = true
            })
            .addCase(holdOrder.fulfilled, (state, action) => {
                state.isLoading = false
                state.isSuccess = true
                state.message = action.payload.message
            })
            .addCase(holdOrder.rejected, (state, action) => {
                state.isLoading = false
                state.isError = true
                state.message = action.payload
            })
            // Resume Order
            .addCase(resumeOrder.pending, (state) => {
                state.isLoading = true
            })
            .addCase(resumeOrder.fulfilled, (state, action) => {
                state.isLoading = false
                state.isSuccess = true
                state.message = action.payload.message
            })
            .addCase(resumeOrder.rejected, (state, action) => {
                state.isLoading = false
                state.isError = true
                state.message = action.payload
            })
            // Get Held Orders
            .addCase(getHeldOrders.pending, (state) => {
                state.isLoading = true
            })
            .addCase(getHeldOrders.fulfilled, (state, action) => {
                state.isLoading = false
                state.isSuccess = true
                state.heldOrders = action.payload
            })
            .addCase(getHeldOrders.rejected, (state, action) => {
                state.isLoading = false
                state.isError = true
                state.message = action.payload
            })
    }
})

export const { reset, clearOrder } = posBusinessSlice.actions

export default posBusinessSlice.reducer
